// src/components/users/UserBlockModal/UserBlockModal.tsx
import React, { useState } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import { Block as BlockIcon, Info as InfoIcon } from '@mui/icons-material';
import { User } from '../../../types/user.types';
import { ConfirmationModal } from '../../ui/Modal';
import { colors } from '../../../styles/theme/colors';

export interface UserBlockModalProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onConfirm: (reason?: string) => Promise<void>;
  loading?: boolean;
}

const UserBlockModal: React.FC<UserBlockModalProps> = ({
  open,
  user,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [isBlocking, setIsBlocking] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  const handleClose = () => {
    if (!isBlocking) {
      setBlockReason('');
      onClose();
    }
  };

  const handleConfirm = async () => {
    setIsBlocking(true);
    try {
      await onConfirm(blockReason || undefined);
      setBlockReason('');
      onClose();
    } catch (error) {
      console.error('Error blocking user:', error);
    } finally {
      setIsBlocking(false);
    }
  };

  if (!user) return null;

  const customContent = (
    <Box
      sx={{
        mt: 2,
        textAlign: 'left',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          backgroundColor: 'rgba(245, 158, 11, 0.06)',
          borderRadius: '8px',
          padding: '10px 14px',
          mb: 2,
        }}
      >
        <InfoIcon sx={{ fontSize: 18, color: '#f59e0b', mt: '2px', flexShrink: 0 }} />
        <Box>
          <Typography variant="body2" sx={{ color: '#f59e0b', mb: 1 }}>
            Impacto del bloqueo:
          </Typography>
          <Typography variant="body2" component="div" sx={{ color: '#f59e0b', lineHeight: 1.5 }}>
            • El usuario no podrá iniciar sesión en el sistema
          </Typography>
          <Typography variant="body2" component="div" sx={{ color: '#f59e0b', lineHeight: 1.5 }}>
            • Se mantendrán sus datos y roles asignados
          </Typography>
          <Typography variant="body2" component="div" sx={{ color: '#f59e0b', lineHeight: 1.5 }}>
            • El usuario puede ser desbloqueado en cualquier momento
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          p: 2.5,
          borderRadius: '8px',
          backgroundColor: colors.grey[50],
          border: `1px solid ${colors.grey[200]}`,
          mb: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            fontSize: '0.75rem',
            fontWeight: 500,
            display: 'block',
            mb: 1,
          }}
        >
          Usuario
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: colors.text.primary,
            mb: 0.5,
          }}
        >
          {user.email}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: colors.text.secondary,
            fontSize: '0.8125rem',
          }}
        >
          {`${user.firstName} ${user.lastName}`.trim() || 'Sin nombre'}
        </Typography>
        {user.roles && user.roles.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: colors.text.secondary,
                fontSize: '0.75rem',
                fontWeight: 500,
                display: 'block',
                mb: 0.5,
              }}
            >
              Roles asignados: {user.roles.length}
            </Typography>
          </Box>
        )}
      </Box>

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Razón del bloqueo (opcional)"
        value={blockReason}
        onChange={(e) => setBlockReason(e.target.value)}
        placeholder="Ej: Violación de políticas, solicitud del usuario, etc."
        sx={{ mt: 2 }}
        disabled={isBlocking || loading}
      />
    </Box>
  );

  return (
    <ConfirmationModal
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={`Bloquear Usuario: ${user.email}`}
      message="¿Desea bloquear este usuario?"
      confirmText={isBlocking ? 'Bloqueando...' : 'Confirmar Bloqueo'}
      cancelText="Cancelar"
      variant="warning"
      icon={<BlockIcon />}
      loading={isBlocking || loading}
      requireCheckbox={true}
      checkboxLabel="Entiendo el impacto del bloqueo"
      customContent={customContent}
    />
  );
};

export default UserBlockModal;
